// /share/script/:token (HD-9): a shared script, read-only, in the review frame (DESIGN rev 2.1 §2.6 Review set, §3.3).
// ReviewShell frame mode, light by default (no monitor; tierKey "script"); this page draws the breadcrumb bar with the
// tier toggle, then the script in a Portal column. The API takes no name or email (GET /api/share-links/scripts/:token is
// the only call), so the page asks for none: the old name and email gate was never sent anywhere. No comments: there is
// no comment API for scripts.
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import BreadcrumbBar from '../ui/BreadcrumbBar';
import ErrorState from '../ui/ErrorState';
import PageHeader from '../ui/PageHeader';
import Skeleton from '../ui/Skeleton';
import Tag from '../ui/Tag';
import ScriptReadView from '../components/ScriptReadView';
import { TierToggle } from '../shell/ReviewShell';
import { clean } from '../shell/copy';

// The format as a Tag; a legacy body with no format ('None') shows none.
const FORMAT_NAMES = { Skit: 'Skit', TH: 'Talking head', AI: 'AI', Mockumentary: 'Mockumentary', Longform: 'Longform', Recipe: 'Recipe' };

export default function ScriptShareView() {
  const { token } = useParams();
  const [state, setState] = useState('loading');   // loading | data | inactive | failed
  const [script, setScript] = useState(null);

  const load = useCallback(() => {
    setState('loading');
    api.get(`/api/share-links/scripts/${token}`)
      .then(res => { setScript(res.data); setState('data'); })
      .catch(err => setState(err.response?.status === 404 ? 'inactive' : 'failed'));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const format = script?.scriptBody?.format;
  const crumbs = state === 'data' ? [clean(script.clientName), clean(script.scriptName)].filter(Boolean) : ['Shared script'];

  return (
    <div className="ui ui-review ui-review--no-comments">
      <BreadcrumbBar crumbs={crumbs} actions={<TierToggle />} />
      {state === 'loading' && (
        <main className="ui-page ui-page--portal" aria-busy="true">
          <div className="ui-stack ui-ph"><Skeleton width="40%" height="28px" label="Loading the script" /><Skeleton width="25%" /></div>
          <div className="ui-stack">{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} variant="block" height="44px" />)}</div>
        </main>
      )}
      {state === 'inactive' && (
        <div className="ui-review-msg">
          <h1 className="ui-heading">This link is no longer active</h1>
          <p className="ui-p ui-text-2">The share link was turned off, or the script was removed. Ask the person who sent it for a new one.</p>
        </div>
      )}
      {state === 'failed' && (
        <div className="ui-review-msg">
          <ErrorState title="The script did not load" message="Check your connection and try again." onRetry={load} />
        </div>
      )}
      {state === 'data' && (
        <main className="ui-page ui-page--portal ui-page--read">
          <PageHeader title={clean(script.scriptName)}
            meta={script.writerName ? `Written by ${clean(script.writerName)}` : undefined}
            actions={(
              <>
                {script.clientName && <Tag>{clean(script.clientName)}</Tag>}
                {FORMAT_NAMES[format] && <Tag>{FORMAT_NAMES[format]}</Tag>}
              </>
            )} />
          <ScriptReadView body={script.scriptBody} />
        </main>
      )}
    </div>
  );
}
